import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { publicApi } from '@/lib/api';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const NAV_LINKS = [
  { to: '/',           label: 'Feed'      },
  { to: '/categories', label: 'Categories' },
  { to: '/tags',       label: 'Tags'      },
  { to: '/search',     label: 'Search'    },
  { to: '/about',      label: 'About'     },
];

const CATEGORY_LINKS = [
  { to: '/categories/prompt-injection',   label: 'Prompt Injection'   },
  { to: '/categories/jailbreaks',         label: 'Jailbreaks'         },
  { to: '/categories/model-theft',        label: 'Model Theft'        },
  { to: '/categories/data-poisoning',     label: 'Data Poisoning'     },
  { to: '/categories/adversarial-inputs', label: 'Adversarial Inputs' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Newsletter inline form
// ─────────────────────────────────────────────────────────────────────────────

function NewsletterForm() {
  const [email, setEmail] = useState('');
  const [done,  setDone]  = useState(false);

  const { mutate, isPending } = useMutation({
    mutationFn: (e: string) => publicApi.subscribeNewsletter(e),
    onSuccess:  () => { setDone(true); setEmail(''); },
  });

  if (done) {
    return (
      <p className="font-mono text-xs text-terminal-green">
        ✓ Subscribed. Critical vuln alerts only. Unsubscribe anytime.
      </p>
    );
  }

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); mutate(email); }}
      className="flex gap-2"
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        className="min-w-0 flex-1 rounded border border-surface-border bg-bg-900 px-3 py-1.5 font-mono text-xs text-text-primary placeholder:text-text-muted focus:border-terminal-green focus:outline-none focus:ring-1 focus:ring-terminal-green transition-colors"
      />
      <button
        type="submit"
        disabled={isPending}
        className="shrink-0 rounded border border-red-500 bg-red-500/10 px-3 py-1.5 font-mono text-xs text-red-400 hover:bg-red-500/20 disabled:opacity-50 transition-colors"
      >
        {isPending ? '…' : 'Subscribe'}
      </button>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Social icons
// ─────────────────────────────────────────────────────────────────────────────

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.259 5.629L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
    </svg>
  );
}

function RSSIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
      <path d="M6.18 15.64a2.18 2.18 0 0 1 2.18 2.18C8.36 19.01 7.38 20 6.18 20C4.98 20 4 19.01 4 17.82a2.18 2.18 0 0 1 2.18-2.18M4 4.44A15.56 15.56 0 0 1 19.56 20h-2.83A12.73 12.73 0 0 0 4 7.27V4.44m0 5.66a9.9 9.9 0 0 1 9.9 9.9h-2.83A7.07 7.07 0 0 0 4 12.93V10.1z" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Footer
// ─────────────────────────────────────────────────────────────────────────────

export default function Footer() {
  return (
    <footer className="border-t border-surface-border bg-bg-950">
      <div className="mx-auto max-w-7xl px-4 py-10 lg:px-6">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">

          {/* Brand */}
          <div className="space-y-3">
            <div className="flex items-center">
              <span className="font-mono text-base font-bold text-text-primary">PROMPT</span>
              <span className="font-mono text-base font-bold text-red-400">//</span>
              <span className="font-mono text-base font-bold text-text-primary">HIJACK</span>
            </div>
            <p className="font-mono text-xs leading-relaxed text-text-muted">
              The Onion, but for CVEs.<br />
              Satirical AI security journalism<br />
              for people who read commit messages.
            </p>
            <div className="flex items-center gap-3 pt-1">
              <a
                href="https://x.com/prompthijack"
                target="_blank"
                rel="noopener noreferrer"
                className="text-text-muted hover:text-text-primary transition-colors"
                aria-label="X / Twitter"
              >
                <XIcon />
              </a>
              <a
                href="/feed.rss"
                className="text-text-muted hover:text-terminal-green transition-colors"
                aria-label="RSS Feed"
              >
                <RSSIcon />
              </a>
            </div>
          </div>

          {/* Nav */}
          <div className="space-y-3">
            <h3 className="font-mono text-xs font-semibold uppercase tracking-widest text-text-muted">
              Navigate
            </h3>
            <ul className="space-y-1.5">
              {NAV_LINKS.map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="font-mono text-xs text-text-secondary hover:text-terminal-green transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div className="space-y-3">
            <h3 className="font-mono text-xs font-semibold uppercase tracking-widest text-text-muted">
              Categories
            </h3>
            <ul className="space-y-1.5">
              {CATEGORY_LINKS.map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="font-mono text-xs text-text-secondary hover:text-terminal-green transition-colors"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div className="space-y-3">
            <h3 className="font-mono text-xs font-semibold uppercase tracking-widest text-text-muted">
              Threat Intel
            </h3>
            <p className="font-mono text-xs text-text-muted">
              Satirical vuln advisories you'll forward to colleagues who won't get the joke.
            </p>
            <NewsletterForm />
          </div>
        </div>
      </div>

      {/* Copyright bar */}
      <div className="border-t border-surface-border">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-4 py-3 sm:flex-row lg:px-6">
          <p className="font-mono text-[10px] text-text-muted">
            © {new Date().getFullYear()} PromptHijack.com — All vulnerabilities are fictitious. Any resemblance to real exploits is entirely intentional.
          </p>
          <div className="flex gap-4">
            <Link to="/privacy" className="font-mono text-[10px] text-text-muted hover:text-text-secondary transition-colors">
              Privacy
            </Link>
            <Link to="/terms" className="font-mono text-[10px] text-text-muted hover:text-text-secondary transition-colors">
              Terms
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
